"use client";

import { useTransition } from "react";
import { useTranslations } from "next-intl";
import { trackLeadAction } from "@/lib/actions/leads";
import { track, LEAD_EVENTS } from "@/lib/analytics";
import PackageCardContent, {
  PACKAGE_CARD_CLASS,
  packagePriceLabel,
  type PackageDisplay,
} from "@/components/ui/PackageCardContent";
import {
  CONTACT_PREFILL_SERVICE_EVENT,
  type ContactPrefillServiceDetail,
} from "@/lib/events";

interface Props {
  pkg: {
    name: string;
    description: string;
    price: string;
  };
  professionalId: string;
  /** Translated name/description to show; `pkg.name` stays the professional's own. */
  display?: PackageDisplay;
  /** Plain-text name for the accessible label, in the page's language. */
  displayName?: string;
}

/**
 * Package card that tracks a package_click lead on interaction, prefills the
 * contact form's "Service Requested" dropdown with this package, and scrolls
 * the user down to the form.
 *
 * The visual card lives in `PackageCardContent`, shared with the logged-out
 * peek preview and the admin applicant preview.
 */
export default function PackageCard({ pkg, professionalId, display, displayName }: Props) {
  const t = useTranslations("profile");
  const [, startTransition] = useTransition();

  const labels = {
    from: t("priceFrom"),
    free: t("free"),
    contactForPrice: t("contactForPrice"),
  };

  function handleClick() {
    // Track non-blocking
    startTransition(() => {
      trackLeadAction(professionalId, "package_click").catch(() => {});
    });
    track(LEAD_EVENTS.packageClick, { professionalId, package: pkg.name });
    // Tell the contact form which package was clicked so it can pre-select
    // the matching option in the Service Requested dropdown.
    window.dispatchEvent(
      new CustomEvent<ContactPrefillServiceDetail>(CONTACT_PREFILL_SERVICE_EVENT, {
        detail: { service: pkg.name },
      }),
    );
    // Scroll to contact form
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.getElementById("contact")?.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth", block: "start" });
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`group ${PACKAGE_CARD_CLASS} w-full text-left transition-colors hover:border-gold cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold`}
      aria-label={t("selectPackage", {
        name: displayName ?? pkg.name,
        price: packagePriceLabel(pkg.price, labels),
      })}
    >
      <PackageCardContent pkg={pkg} labels={labels} display={display} interactive />
    </button>
  );
}
